// /components/friendship/Friendship.jsx
import './friendship.css';
import { useEffect, useRef, useState, useContext, useCallback } from 'react';
import useClickOutside from '../../clickOutside';
import { AuthContext } from '../../context/AuthContext';
import axiosConn from '../../axiosConn';
import { Link } from 'react-router-dom';
import { useGetCurrPlayer } from '../../hooks/useGetCurrPlayer';
import { useTheme } from '../../context/ThemeContext';

/** Small visual tokens layered over existing CSS classes (no breaks) */
const tokens = (dark) => ({
  // general text colors
  text: dark ? '#e5edf7' : '#1f2937',
  textSub: dark ? '#cbd5e1' : '#4b5563',

  // button backgrounds (sit atop your .gray_btn /.blue_btn /.jgpurple_btn)
  btn: {
    gray: {
      background: dark ? 'rgba(255,255,255,0.08)' : '#f3f4f6',
      border: dark ? '1px solid rgba(255,255,255,0.14)' : '1px solid #e5e7eb',
      color: dark ? '#e5edf7' : '#1f2937',
    },
    blue: {
      background: dark ? 'rgba(59,130,246,0.18)' : '#2563eb',
      border: dark ? '1px solid rgba(59,130,246,0.45)' : '1px solid #1d4ed8',
      color: dark ? '#eaf2ff' : '#ffffff',
    },
    jgpurple: {
      background: dark ? 'rgba(139,92,246,0.20)' : '#7c3aed',
      border: dark ? '1px solid rgba(139,92,246,0.45)' : '1px solid #6d28d9',
      color: dark ? '#f5f0ff' : '#ffffff',
    },
  },

  // menu surfaces
  menuBg: dark ? 'rgba(17,24,39,0.96)' : '#ffffff',
  menuBorder: dark ? '1px solid rgba(255,255,255,0.12)' : '1px solid #e5e7eb',
  menuItemHover: dark ? 'rgba(255,255,255,0.06)' : '#f3f4f6',

  // subtle shadow to float menus over cover
  shadow: dark
    ? '0 8px 24px rgba(0,0,0,0.45), 0 2px 6px rgba(0,0,0,0.35)'
    : '0 8px 24px rgba(0,0,0,0.08), 0 2px 6px rgba(0,0,0,0.06)',

  // icon filter so dark theme shows white-ish pictos
  iconFilter: dark ? 'invert(1) brightness(1.1)' : 'none',
});

/** Shared button style overlay (keeps your classes intact) */
const btnStyle = (base) => ({
  ...base,
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  padding: '8px 12px',
  borderRadius: 10,
  fontSize: 13,
  fontWeight: 700,
  letterSpacing: '.2px',
  cursor: 'pointer',
  userSelect: 'none',
  textDecoration: 'none',
});

/** Menu wrapper style overlay */
const menuWrapStyle = (t) => ({
  background: t.menuBg,
  border: t.menuBorder,
  boxShadow: t.shadow,
  borderRadius: 12,
  overflow: 'hidden',
  minWidth: 200,
});

const menuItemStyle = (t) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '10px 12px',
  cursor: 'pointer',
  color: t.text,
});

export default function Friendship({ player1 }) {
  const [friendsMenu, setFriendsMenu] = useState(false);
  const [respondMenu, setRespondMenu] = useState(false);
  const menu = useRef(null);
  const menu1 = useRef(null);
  useClickOutside(menu, () => setFriendsMenu(false));
  useClickOutside(menu1, () => setRespondMenu(false));
  const { player, dispatch } = useContext(AuthContext);

  const [visitedPlayer, setVisitedPlayer] = useState({});
  const [currPlayer, setCurrPlayer] = useState({});
  const { darkTheme } = useTheme();
  const t = tokens(darkTheme);

  // player follows player1.
  const [followed, setFollowed] = useState(
    player.data.player.followings.includes(player1?._id),
  );

  // we are in each other's friends array. we are friends.
  const [isFriends, setIsFriends] = useState(false);

  // player sent player1 a request.
  const [requestSent, setRequestSent] = useState(false);

  // player received a request from player1.
  const [requestReceived, setRequestReceived] = useState(false);
  const [isDone1, setIsDone1] = useState(false);
  const [isDone2, setIsDone2] = useState(false);
  const [isDone3, setIsDone3] = useState(false);
  const [convoId, setConvoId] = useState('');

  // Close menus on ESC for polish
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setFriendsMenu(false);
        setRespondMenu(false);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    let mounted = true;
    const getVisitedPlayer = async () => {
      try {
        const res = await axiosConn.get(
          `players/?playerId=${player1.playerId}`,
          {
            headers: { Authorization: `Bearer ${player.token}` },
          },
        );
        if (!mounted) return;
        setVisitedPlayer(res.data.data.data[0]);
        setIsDone1(true);
      } catch (err) {
        console.log(err);
      }
    };
    getVisitedPlayer();
    return () => {
      mounted = false;
    };
  }, [player.data.player._id, player.token, player1.playerId]);

  const onSuccess = useCallback(() => {}, []);
  const onError = useCallback(() => {}, []);

  const { data, status } = useGetCurrPlayer(onSuccess, onError, player);

  useEffect(() => {
    let mounted = true;
    if (status === 'success') {
      if (!mounted) return;
      setCurrPlayer(data);
      setIsDone2(true);
    }
    return () => {
      mounted = false;
    };
  }, [status, data]);

  useEffect(() => {
    if (currPlayer && visitedPlayer && isDone1 && isDone2) {
      setFollowed(currPlayer.followings?.includes(visitedPlayer?._id));
      setIsFriends(
        currPlayer.friends?.includes(visitedPlayer?._id) &&
          visitedPlayer?.friends?.includes(currPlayer?._id),
      );
      setRequestSent(visitedPlayer?.requests?.includes(currPlayer._id));
      setRequestReceived(currPlayer.requests?.includes(visitedPlayer._id));
      setIsDone3(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currPlayer?._id,
    currPlayer?.followings,
    currPlayer?.friends,
    currPlayer?.requests,
    isDone1,
    isDone2,
    isDone3,
    player,
    player1,
    visitedPlayer,
  ]);

  const acceptRequest = async () => {
    try {
      await axiosConn.put(
        `/players/acceptRequest/${player1._id}`,
        { id: player.data.player._id },
        { headers: { Authorization: `Bearer ${player.token}` } },
      );
      const currentPlayer = JSON.parse(localStorage.getItem('player'));
      currentPlayer.data.player.friends.push(player1._id);
      currentPlayer.data.player.followings.push(player1._id);
      currentPlayer.data.player.followers.push(player1._id);
      localStorage.setItem('player', JSON.stringify(currentPlayer));
      window.location.reload();
      return 'ok';
    } catch (error) {
      console.log(error.response?.data?.message || error.message);
      return error.response?.data?.message || 'error';
    }
  };

  const addFriend = async () => {
    try {
      await axiosConn.put(
        `players/addFriend/${player1._id}`,
        { id: player.data.player._id },
        { headers: { Authorization: `Bearer ${player.token}` } },
      );
      return 'ok';
    } catch (error) {
      console.log(error.response?.data?.message || error.message);
      return error.response?.data?.message || 'error';
    }
  };

  const cancelRequest = async () => {
    try {
      await axiosConn.put(
        `players/cancelRequest/${player1._id}`,
        { id: player.data.player._id },
        { headers: { Authorization: `Bearer ${player.token}` } },
      );
      return 'ok';
    } catch (error) {
      console.log(error.response?.data?.message || error.message);
      return error.response?.data?.message || 'error';
    }
  };

  const deleteRequest = async () => {
    try {
      await axiosConn.put(
        `players/deleteRequest/${player1._id}`,
        { id: player.data.player._id },
        { headers: { Authorization: `Bearer ${player.token}` } },
      );
      return 'ok';
    } catch (error) {
      console.log(error.response?.data?.message || error.message);
      return error.response?.data?.message || 'error';
    }
  };

  const unfriend = async () => {
    try {
      await axiosConn.put(
        `players/unfriend/${player1._id}`,
        { id: player.data.player._id },
        { headers: { Authorization: `Bearer ${player.token}` } },
      );
      window.location.reload();
      return 'ok';
    } catch (error) {
      console.log(error.response?.data?.message || error.message);
      return error.response?.data?.message || 'error';
    }
  };

  const followUnfollow = async () => {
    try {
      if (followed) {
        await axiosConn.put(
          `players/${player1._id}/unfollow`,
          { id: player.data.player._id },
          { headers: { Authorization: `Bearer ${player.token}` } },
        );
        dispatch({ type: 'UNFOLLOW', payload: player1._id });
      } else {
        await axiosConn.put(
          `players/${player1._id}/follow`,
          { id: player.data.player._id },
          { headers: { Authorization: `Bearer ${player.token}` } },
        );
        dispatch({ type: 'FOLLOW', payload: player1._id });
      }
      setFollowed((s) => !s);
    } catch (err) {
      console.log(err);
    }
  };

  const addFriendHandler = async () => {
    setRequestSent(true);
    setFollowed(true);
    await addFriend(player1._id, player.token);
  };
  const cancelRequestHandler = async () => {
    setRequestSent(false);
    setFollowed(false);
    await cancelRequest(player1._id, player.token);
  };
  const followHandler = async () => {
    await followUnfollow();
  };
  const unfollowHandler = async () => {
    await followUnfollow();
  };

  const acceptRequestHandler = async () => {
    setIsFriends(true);
    setFollowed(true);
    setRequestSent(false);
    setRequestReceived(false);
    await acceptRequest(player1._id, player.token);
  };
  const unfriendHandler = async () => {
    setIsFriends(false);
    setFollowed(false);
    setRequestSent(false);
    setRequestReceived(false);
    await unfriend(player1._id, player.token);
  };
  const deleteRequestHandler = async () => {
    setIsFriends(false);
    setFollowed(false);
    setRequestSent(false);
    setRequestReceived(false);
    await deleteRequest(player1._id, player.token);
  };

  const goToMessenger = (cid) => {
    setConvoId(cid);
    window.location.href = `/messenger/${cid}`;
  };

  const createConversationIfNone = async () => {
    try {
      await axiosConn
        .get(`/conversations/find/${currPlayer._id}/${visitedPlayer._id}`, {
          headers: { Authorization: `Bearer ${player.token}` },
        })
        .then(async (res) => {
          if (typeof res.data === 'undefined' || res.data === null) {
            const res2 = await axiosConn.post(
              `/conversations/`,
              { senderId: currPlayer._id, receiverId: visitedPlayer._id },
              { headers: { Authorization: `Bearer ${player.token}` } },
            );
            goToMessenger(res2.data._id);
          } else {
            goToMessenger(res.data._id);
          }
        });
    } catch (err) {
      console.log(err);
    }
  };

  return (
    <>
      {isDone1 &&
        isDone2 &&
        isDone3 &&
        typeof visitedPlayer !== 'undefined' &&
        typeof currPlayer !== 'undefined' && (
          <div
            className="friendship"
            style={{
              color: t.text,
              // 👇 Add a little space above on both mobile & desktop
              marginTop: 12,
            }}
          >
            <div className="flex" style={{ gap: 8, flexWrap: 'wrap' }}>
              {isFriends ? (
                <div
                  className="friends_menu_wrap"
                  style={{ position: 'relative' }}
                >
                  <button
                    className="gray_btn"
                    onClick={() => setFriendsMenu((s) => !s)}
                    style={btnStyle({
                      background: t.btn.gray.background,
                      border: t.btn.gray.border,
                      color: t.btn.gray.color,
                    })}
                    aria-haspopup="menu"
                    aria-expanded={friendsMenu}
                    aria-label="Friends menu"
                  >
                    <img
                      src="https://res.cloudinary.com/joegalaxy/image/upload/q_auto/v1660852717/Images/friends_ndlpv1.png"
                      alt=""
                      style={{ filter: t.iconFilter, width: 18, height: 18 }}
                    />
                    <span style={{ color: t.btn.gray.color }}>Friends</span>
                  </button>
                  {friendsMenu && (
                    <div
                      className={
                        darkTheme
                          ? 'open_cover_menu darkTheme blackBg'
                          : 'open_cover_menu'
                      }
                      ref={menu}
                      role="menu"
                      style={{
                        ...menuWrapStyle(t),
                        position: 'absolute',
                        top: 'calc(100% + 6px)',
                        left: 0,
                        zIndex: 20,
                      }}
                    >
                      <div
                        className={
                          darkTheme
                            ? 'darkTheme blackBg open_cover_menu_item hover1'
                            : 'open_cover_menu_item hover1'
                        }
                        onClick={() => unfriendHandler()}
                        role="menuitem"
                        style={menuItemStyle(t)}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.background = t.menuItemHover)
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.background = 'transparent')
                        }
                      >
                        <i className="unfriend_outlined_icon" />
                        <span style={{ color: t.text }}>Unfriend</span>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                !requestSent &&
                !requestReceived && (
                  <button
                    className="jgpurple_btn"
                    onClick={() => addFriendHandler()}
                    style={btnStyle({
                      background: t.btn.jgpurple.background,
                      border: t.btn.jgpurple.border,
                      color: t.btn.jgpurple.color,
                    })}
                    aria-label="Add Friend"
                  >
                    <div className="friend_img">
                      <img
                        src="https://res.cloudinary.com/joegalaxy/image/upload/q_auto/v1660853126/Images/addFriend_xcfhya.png"
                        alt=""
                        className="img_bro"
                        style={{ filter: t.iconFilter, width: 18, height: 18 }}
                      />
                    </div>
                    <span style={{ color: t.btn.jgpurple.color }}>
                      Add Friend
                    </span>
                  </button>
                )
              )}

              {requestSent ? (
                <button
                  className="jgpurple_btn"
                  onClick={() => cancelRequestHandler()}
                  style={btnStyle({
                    background: t.btn.jgpurple.background,
                    border: t.btn.jgpurple.border,
                    color: t.btn.jgpurple.color,
                  })}
                  aria-label="Cancel Request"
                >
                  <img
                    src="https://res.cloudinary.com/joegalaxy/image/upload/q_auto/v1660853174/Images/cancelRequest_rjism1.png"
                    alt=""
                    style={{ filter: t.iconFilter, width: 18, height: 18 }}
                  />
                  <span style={{ color: t.btn.jgpurple.color }}>
                    Cancel Request
                  </span>
                </button>
              ) : (
                requestReceived && (
                  <div
                    className="friends_menu_wrap"
                    style={{ position: 'relative' }}
                  >
                    <button
                      className="gray_btn"
                      onClick={() => setRespondMenu((s) => !s)}
                      style={btnStyle({
                        background: t.btn.gray.background,
                        border: t.btn.gray.border,
                        color: t.btn.gray.color,
                      })}
                      aria-haspopup="menu"
                      aria-expanded={respondMenu}
                      aria-label="Respond to request"
                    >
                      <img
                        src="https://res.cloudinary.com/joegalaxy/image/upload/q_auto/v1660853206/Images/friends_p9ox8p.png"
                        alt=""
                        style={{ filter: t.iconFilter, width: 18, height: 18 }}
                      />
                      <span style={{ color: t.btn.gray.color }}>Respond</span>
                    </button>
                    {respondMenu && (
                      <div
                        className={
                          darkTheme
                            ? 'open_cover_menu darkTheme blackBg'
                            : 'open_cover_menu'
                        }
                        ref={menu1}
                        role="menu"
                        style={{
                          ...menuWrapStyle(t),
                          position: 'absolute',
                          top: 'calc(100% + 6px)',
                          left: 0,
                          zIndex: 20,
                        }}
                      >
                        <div
                          className={
                            darkTheme
                              ? 'darkTheme blackBg open_cover_menu_item hover1'
                              : 'open_cover_menu_item hover1'
                          }
                          onClick={() => acceptRequestHandler()}
                          role="menuitem"
                          style={menuItemStyle(t)}
                          onMouseEnter={(e) =>
                            (e.currentTarget.style.background = t.menuItemHover)
                          }
                          onMouseLeave={(e) =>
                            (e.currentTarget.style.background = 'transparent')
                          }
                        >
                          <span style={{ color: t.text }}>Confirm</span>
                        </div>
                        <div
                          className={
                            darkTheme
                              ? 'darkTheme blackBg open_cover_menu_item hover1'
                              : 'open_cover_menu_item hover1'
                          }
                          onClick={() => deleteRequestHandler()}
                          role="menuitem"
                          style={menuItemStyle(t)}
                          onMouseEnter={(e) =>
                            (e.currentTarget.style.background = t.menuItemHover)
                          }
                          onMouseLeave={(e) =>
                            (e.currentTarget.style.background = 'transparent')
                          }
                        >
                          <span style={{ color: t.text }}>Delete</span>
                        </div>
                      </div>
                    )}
                  </div>
                )
              )}

              {followed ? (
                <button
                  className="gray_btn"
                  onClick={() => unfollowHandler()}
                  style={btnStyle({
                    background: t.btn.gray.background,
                    border: t.btn.gray.border,
                    color: t.btn.gray.color,
                  })}
                  aria-label="Unfollow"
                >
                  <img
                    src="https://res.cloudinary.com/joegalaxy/image/upload/q_auto/v1660853252/Images/follow_axxydp.png"
                    alt=""
                    style={{ filter: t.iconFilter, width: 18, height: 18 }}
                  />
                  <span style={{ color: t.btn.gray.color }}>Following</span>
                </button>
              ) : (
                <button
                  className="blue_btn"
                  onClick={() => followHandler()}
                  style={btnStyle({
                    background: t.btn.blue.background,
                    border: t.btn.blue.border,
                    color: t.btn.blue.color,
                  })}
                  aria-label="Follow"
                >
                  <img
                    src="https://res.cloudinary.com/joegalaxy/image/upload/q_auto/v1660853252/Images/follow_axxydp.png"
                    alt=""
                    style={{ filter: t.iconFilter, width: 18, height: 18 }}
                  />
                  <span style={{ color: t.btn.blue.color }}>Follow</span>
                </button>
              )}

              <button
                className={isFriends ? 'blue_btn' : 'gray_btn'}
                onClick={() => createConversationIfNone()}
                style={btnStyle(
                  isFriends
                    ? {
                        background: t.btn.blue.background,
                        border: t.btn.blue.border,
                        color: t.btn.blue.color,
                      }
                    : {
                        background: t.btn.gray.background,
                        border: t.btn.gray.border,
                        color: t.btn.gray.color,
                      },
                )}
                aria-label="Message"
              >
                <img
                  src="https://res.cloudinary.com/joegalaxy/image/upload/q_auto/v1660853304/Images/message_gud2nk.png"
                  alt=""
                  style={{
                    filter: t.iconFilter,
                    width: 18,
                    height: 18,
                  }}
                />
                {isFriends ? (
                  <Link
                    to={`/messenger/${convoId}`}
                    style={{ textDecoration: 'none', color: t.btn.blue.color }}
                  >
                    <span>Message</span>
                  </Link>
                ) : (
                  <span style={{ color: t.btn.gray.color }}>Message</span>
                )}
              </button>
            </div>
          </div>
        )}
    </>
  );
}
